import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { customerApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';

export default function ResetWithCode() {
  const navigate = useNavigate();
  const [step, setStep] = useState('code'); // code -> password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const continueToPassword = (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{4}$/.test(code.trim())) {
      setError('Enter the 4-digit code your staff member gave you');
      return;
    }
    setStep('password');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await customerApi.post('/auth/customer/reset-password-with-code', {
        email: email.trim(),
        code: code.trim(),
        newPassword,
      });
      navigate('/cxlogin');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/logo-wordmark.png"
            alt="The Tiffin by The Virsa"
            className="mx-auto h-16 object-contain"
          />
        </div>

        {step === 'code' ? (
          <form onSubmit={continueToPassword} className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 space-y-4">
            <h2 className="font-heading text-xl font-semibold text-charcoal">Reset your password</h2>
            <p className="text-sm text-muted-ink" data-testid="reset-code-instructions">
              Ask a staff member at the counter, or call the store, and they'll generate a 4-digit code for
              you instantly. Enter your account email and that code below.
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="rwc-email">Email</Label>
              <Input
                id="rwc-email"
                type="email"
                required
                data-testid="reset-code-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rwc-code">4-digit code</Label>
              <Input
                id="rwc-code"
                inputMode="numeric"
                maxLength={4}
                required
                data-testid="reset-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                className="text-center text-2xl tracking-[0.5em] font-heading"
                placeholder="0000"
              />
            </div>

            {error && <p className="text-sm text-destructive" data-testid="reset-code-error">{error}</p>}

            <Button
              type="submit"
              data-testid="reset-code-continue-btn"
              className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
            >
              Continue
            </Button>

            <p className="text-sm text-center">
              <Link to="/cxlogin" className="text-coriander hover:underline">Back to login</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 space-y-4">
            <h2 className="font-heading text-xl font-semibold text-charcoal">Set a new password</h2>

            <div className="space-y-1.5">
              <Label htmlFor="rwc-new">New password</Label>
              <PasswordInput
                id="rwc-new"
                minLength={6}
                required
                data-testid="reset-code-new-password-input"
                toggleTestId="reset-code-new-password-toggle"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rwc-confirm">Confirm password</Label>
              <PasswordInput
                id="rwc-confirm"
                minLength={6}
                required
                data-testid="reset-code-confirm-password-input"
                toggleTestId="reset-code-confirm-password-toggle"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-destructive" data-testid="reset-code-password-error">{error}</p>}

            <Button
              type="submit"
              disabled={busy}
              data-testid="reset-code-submit-btn"
              className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
            >
              {busy ? 'Updating…' : 'Set New Password'}
            </Button>

            <button
              type="button"
              data-testid="reset-code-back-btn"
              onClick={() => setStep('code')}
              className="text-sm text-center w-full text-coriander hover:underline"
            >
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
